/* eslint-disable import/no-cycle */

import React from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { withTheme } from 'styled-components'

import { fetchCountries } from 'actions/catalog/countries'
import { getProvincesOfCountry } from 'actions/catalog/provinces'
import Dropdown from 'components/dropdown'
import { Label } from 'components/forms/components/styled'
import { getCountry } from 'components/forms/helpers/countries'
import { getCountryOptions, getProvincesOptionsByCountry } from 'components/forms/utils/Helpers'
import Icon, { IconNames } from 'components/icons'
import Input from 'components/input'
import { CAPITAL_FEDERAL_PROVINCE } from 'constants/corporateAgreements'
import e2eClasses from 'helpers/automation'
import { isTablet, isEmpty } from 'helpers/utils'
import { withErrorClassName } from 'hoc/withErrorClassName'
import NAMESPACES from 'localization/constants/namespaces'
import { getCountries } from 'selectors/countries'
import { mapMediaType } from 'selectors/mediaType'
import { getProvinces } from 'selectors/provinces'

import { InputContainer, InputRow } from '../styled'
import { ids } from './ids.js'
import { SectionContainer, TitleWrapper, SectionTitle, IconWrapper } from './styled'

const InputWithErrorClassName = withErrorClassName(Input)
const DropdownWithErrorClassName = withErrorClassName(Dropdown)

const EnterpriseDataFields = ({
  theme,
  mediaType,
  formikBag: { handleChange, handleBlur, setFieldTouched, setFieldValue },
  countries,
  provinces,
  sectionProps: { values = {}, touched = {}, errors = {} },
  ...otherProps
}) => {
  const { enterpriseAddress = {} } = values
  const addressErrors = errors.enterpriseAddress || {}
  const addressTouched = touched.enterpriseAddress || {}

  const {
    t,
    i18n: { language }
  } = useTranslation(NAMESPACES.CORPORATE_AGREEMENTS)

  const countryPageValues = getCountry(countries, language)
  const provincesOptions = getProvincesOptionsByCountry(
    provinces,
    values.enterpriseAddress?.country?.value
  )
  React.useEffect(() => {
    isEmpty(countries)
      ? otherProps.fetchCountries()
      : onCountryChange({ selectedOption: countryPageValues })
  }, [countries])

  const onCountryChange = ({ selectedOption }) => {
    selectedOption.value == 'AR' && otherProps.getProvincesOfCountry(selectedOption.value)
    setFieldValue('enterpriseFields.enterpriseAddress.country', selectedOption)
    setFieldValue('enterpriseFields.enterpriseAddress.state', '', true)
    setFieldTouched('enterpriseFields.enterpriseAddress.state', false)
  }

  return (
    <SectionContainer>
      <TitleWrapper e2eClass={e2eClasses('corporate.enterepriseTitle')}>
        <IconWrapper>
          <Icon name={IconNames.Business} color={theme.colors.blue} size={24} />
        </IconWrapper>
        <SectionTitle>{t('agreements.request.enterprise-title')}</SectionTitle>
      </TitleWrapper>
      <InputRow>
        <InputContainer>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseName')}
            name="enterpriseFields.enterpriseName"
            labelFontSize="16px"
            label={t('agreements.request.enterprise-name.label')}
            placeholder={t('agreements.request.enterprise-name.placeholder')}
            value={values.enterpriseName}
            error={touched.enterpriseName && errors.enterpriseName}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.enterpriseName}
            role="textbox"
          />
        </InputContainer>
        <InputContainer>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseCuit')}
            name="enterpriseFields.enterpriseCuit"
            labelFontSize="16px"
            label={t('agreements.request.enterprise-cuit.label')}
            placeholder={t('agreements.request.enterprise-cuit.placeholder')}
            value={values.enterpriseCuit}
            error={touched.enterpriseCuit && errors.enterpriseCuit}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.enterpriseCuit}
            role="textbox"
          />
        </InputContainer>
        <InputContainer>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseActivity')}
            name="enterpriseFields.enterpriseActivity"
            labelFontSize="16px"
            label={t('agreements.request.enterprise-activity.label')}
            placeholder={t('agreements.request.enterprise-activity.label')}
            value={values.enterpriseActivity}
            error={touched.enterpriseActivity && errors.enterpriseActivity}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.enterpriseActivity}
            role="textbox"
          />
        </InputContainer>
      </InputRow>
      <InputRow>
        <InputContainer flexDirection="column">
          <Label
            e2eClass={e2eClasses('corporate.enterpriseCountry')}
            fontSize="14px"
            forId={ids.country}
          >
            {t('label.country')}
          </Label>
          <DropdownWithErrorClassName
            onChange={onCountryChange}
            onBlur={() => setFieldTouched('enterpriseFields.enterpriseAddress.country', true, true)}
            autoComplete="disabled"
            e2eClass={e2eClasses('corporate.enterpriseCountry')}
            name="enterpriseFields.enterpriseAddress.country"
            placeholder={t('label.country')}
            options={getCountryOptions(countries)}
            noOptionsMessage={t('label.no-options')}
            value={enterpriseAddress.country}
            error={addressTouched.country && addressErrors.country}
            disabled={isEmpty(countries)}
            fontSize="14px"
            inputId={ids.country}
          />
        </InputContainer>
        <InputContainer flexDirection="column">
          <Label
            e2eClass={e2eClasses('corporate.enterpriseState')}
            fontSize="14px"
            forId={ids.province}
          >
            {t('label.province')}
          </Label>
          <DropdownWithErrorClassName
            onChange={({ selectedOption: selectedState }) => {
              setFieldValue('enterpriseFields.enterpriseAddress.state', selectedState)
              if (selectedState.label.toLowerCase() === CAPITAL_FEDERAL_PROVINCE) {
                setFieldValue('enterpriseFields.enterpriseAddress.city', '')
              }
            }}
            onBlur={() => setFieldTouched('enterpriseFields.enterpriseAddress.state', true, true)}
            autoComplete="disabled"
            e2eClass={e2eClasses('corporate.enterpriseState')}
            name="enterpriseFields.enterpriseAddress.state"
            placeholder={t('label.province')}
            options={provincesOptions}
            noOptionsMessage={t('label.no-options')}
            value={enterpriseAddress.state}
            error={addressTouched.state && addressErrors.state}
            disabled={isEmpty(countries) || isEmpty(provincesOptions) || isEmpty(provinces)}
            fontSize="14px"
            inputId={ids.province}
          />
        </InputContainer>
        <InputContainer>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseCity')}
            name="enterpriseFields.enterpriseAddress.city"
            labelFontSize="16px"
            label={t('label.city')}
            placeholder={t('label.city')}
            value={enterpriseAddress.city}
            error={addressTouched.city && addressErrors.city}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.city}
            role="textbox"
          />
        </InputContainer>
      </InputRow>
      <InputRow>
        <InputContainer flex={isTablet(mediaType) && '0 1 auto'}>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseStreet')}
            name="enterpriseFields.enterpriseAddress.street"
            labelFontSize="16px"
            label={t('label.street')}
            placeholder={t('label.street')}
            value={enterpriseAddress.street}
            error={addressTouched.street && addressErrors.street}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.street}
            role="textbox"
          />
        </InputContainer>
        <InputContainer
          justifyContent="space-between"
          flexDirection="row"
          flex={isTablet(mediaType) && '0 1 auto'}
          flexWrap="wrap"
        >
          <InputContainer flex={isTablet(mediaType) ? '0 1 32%' : 0.4}>
            <InputWithErrorClassName
              light
              e2eClass={e2eClasses('corporate.enterpriseNumber')}
              name="enterpriseFields.enterpriseAddress.number"
              labelFontSize="16px"
              label={t('label.number')}
              placeholder={t('label.number')}
              value={enterpriseAddress.number}
              error={addressTouched.number && addressErrors.number}
              onChange={handleChange}
              onBlur={handleBlur}
              id={ids.number}
              role="textbox"
            />
          </InputContainer>
          <InputContainer flex={isTablet(mediaType) ? '0 1 32%' : 0.25}>
            <InputWithErrorClassName
              light
              e2eClass={e2eClasses('corporate.enterpriseFloor')}
              name="enterpriseFields.enterpriseAddress.floor"
              labelFontSize="16px"
              label={t('label.floor')}
              placeholder={t('label.floor')}
              value={enterpriseAddress.floor}
              error={addressTouched.floor && addressErrors.floor}
              onChange={handleChange}
              onBlur={handleBlur}
              id={ids.floor}
              role="textbox"
            />
          </InputContainer>
          <InputContainer flex={isTablet(mediaType) ? '0 1 32%' : 0.25}>
            <InputWithErrorClassName
              light
              e2eClass={e2eClasses('corporate.enterpriseApartment')}
              name="enterpriseFields.enterpriseAddress.apartment"
              labelFontSize="16px"
              label={t('label.apartment')}
              placeholder={t('label.apartment')}
              value={enterpriseAddress.apartment}
              error={addressTouched.apartment && addressErrors.apartment}
              onChange={handleChange}
              onBlur={handleBlur}
              id={ids.apartment}
              role="textbox"
            />
          </InputContainer>
        </InputContainer>
        <InputContainer flex={isTablet(mediaType) && '0 1 auto'}>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseZipCode')}
            name="enterpriseFields.enterpriseAddress.zipCode"
            labelFontSize="16px"
            label={t('agreements.request.enterprise-zip-code.label')}
            placeholder={t('label.zip-code')}
            value={enterpriseAddress.zipCode}
            error={addressTouched.zipCode && addressErrors.zipCode}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.zipCode}
            role="textbox"
          />
        </InputContainer>
      </InputRow>
      <InputRow isLast>
        <InputContainer>
          <InputWithErrorClassName
            light
            e2eClass={e2eClasses('corporate.enterpriseUrl')}
            name="enterpriseFields.enterpriseUrl"
            labelFontSize="16px"
            label={t('agreements.request.enterprise-web.label')}
            placeholder={t('agreements.request.enterprise-web.label')}
            value={values.enterpriseUrl}
            error={touched.enterpriseUrl && errors.enterpriseUrl}
            onChange={handleChange}
            onBlur={handleBlur}
            id={ids.url}
            role="textbox"
          />
        </InputContainer>
      </InputRow>
    </SectionContainer>
  )
}

const mapStateToProps = state => ({
  mediaType: mapMediaType(state),
  countries: getCountries(state),
  provinces: getProvinces(state)
})
const mapDispatchToProps = {
  fetchCountries,
  getProvincesOfCountry
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withTheme(
    React.memo(
      EnterpriseDataFields,
      (prevProps, { sectionProps: { values, touched, errors }, countries, provinces, mediaType }) =>
        prevProps.sectionProps.values === values &&
        prevProps.sectionProps.touched === touched &&
        prevProps.sectionProps.errors === errors &&
        prevProps.countries === countries &&
        prevProps.provinces === provinces &&
        prevProps.mediaType === mediaType
    )
  )
)
